import React from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, ArrowRight, CheckCircle2, ExternalLink, Lightbulb, SlidersHorizontal, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import AILabShell from "@/components/ai-lab/AILabShell";
import CategoryIcon from "@/components/ai-lab/CategoryIcon";
import CopyButton from "@/components/ai-lab/CopyButton";
import ToolCard from "@/components/ai-lab/ToolCard";
import { CATEGORY_BY_ID, CATEGORIES, ACCENT_CLASSES } from "@/data/ai-lab/categories";
import { TOOLKIT_PAGE_BY_ID } from "@/data/ai-lab/toolkitPages";
import { TASK_BY_ID, tasksForCategory } from "@/data/ai-lab/promptLibrary";
import { TOOL_BY_ID } from "@/data/ai-lab/aiTools";
import { renderWithPlaceholders } from "@/lib/ai-lab/renderTemplate";
import { useBrandProfile } from "@/hooks/useBrandProfile";
import { eventsApi } from "@/lib/ai-lab/api";

const SECTIONS = [
  { id: "what", label: "What you can do" },
  { id: "how", label: "How to do it" },
  { id: "tools", label: "Recommended tools" },
  { id: "prompts", label: "Prompts" },
  { id: "examples", label: "Examples" },
];

/**
 * ToolkitPage — route: /ai-cpg-tools/:categorySlug  (e.g. /ai-cpg-tools/packaging)
 * One generic component renders all 12 toolkit pages from toolkitPages.js.
 * Five sections: WHAT YOU CAN DO · HOW TO DO IT · RECOMMENDED TOOLS · PROMPTS · EXAMPLES.
 */
export default function ToolkitPage() {
  const { categorySlug } = useParams();
  const category = CATEGORIES.find((c) => c.slug === categorySlug);
  const page = category ? TOOLKIT_PAGE_BY_ID[category.id] : null;
  const { profile, hasProfile } = useBrandProfile();

  if (!category || !page) {
    return (
      <AILabShell crumbs={[{ label: "Not found" }]} title="Toolkit not found">
        <p className="text-sm text-muted-foreground">Pick a toolkit from the <Link className="underline" to="/ai-cpg-tools">AI CPG Tools dashboard</Link>.</p>
      </AILabShell>
    );
  }

  const accent = ACCENT_CLASSES[category.accent];
  const featured = page.featuredTaskIds.map((id) => TASK_BY_ID[id]).filter(Boolean);
  const allTasks = tasksForCategory(category.id);
  const more = allTasks.filter((t) => !page.featuredTaskIds.includes(t.id));

  return (
    <AILabShell
      crumbs={[{ label: category.short }]}
      title={
        <span className="flex items-center gap-3"><CategoryIcon icon={category.icon} accent={category.accent} size="lg" />{page.title}</span>
      }
      subtitle={page.subtitle}
      actions={<Button asChild className="bg-violet-600 hover:bg-violet-700"><Link to={`/ai-cpg-tools/prompt-builder?category=${category.id}`}><Wand2 className="mr-2 h-4 w-4" /> Open in Prompt Builder</Link></Button>}
    >
      {/* Section jump nav */}
      <nav className="mb-8 flex gap-1 overflow-x-auto rounded-lg border bg-card p-1 text-sm" aria-label="On this page">
        {SECTIONS.map((s) => (
          <a key={s.id} href={`#${s.id}`} className="shrink-0 rounded-md px-3 py-1.5 text-muted-foreground hover:bg-muted hover:text-foreground">{s.label}</a>
        ))}
      </nav>

      {/* 1 · WHAT YOU CAN DO */}
      <Section id="what" number="01" title="What you can do">
        <p className="max-w-3xl text-base leading-relaxed">{page.whatYouCanDo.intro}</p>
        <ul className="mt-5 grid gap-2 sm:grid-cols-2">
          {page.whatYouCanDo.outcomes.map((o, i) => (
            <li key={i} className="flex items-start gap-2 text-sm"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{o}</li>
          ))}
        </ul>
        {page.whatYouCanDo.reality && (
          <div className="mt-5 flex items-start gap-3 rounded-lg border border-pink-200 bg-pink-50 p-4 text-sm dark:border-pink-900 dark:bg-pink-950/30">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-pink-600" />
            <p><span className="font-semibold">Reality check: </span>{page.whatYouCanDo.reality}</p>
          </div>
        )}
      </Section>

      {/* 2 · HOW TO DO IT */}
      <Section id="how" number="02" title="How to do it">
        <ol className="relative space-y-0 border-l pl-0">
          {page.howToDoIt.map((step) => {
            const task = step.taskId ? TASK_BY_ID[step.taskId] : null;
            const tool = step.toolId ? TOOL_BY_ID[step.toolId] : null;
            return (
              <li key={step.step} className="relative ml-6 pb-6 last:pb-0">
                <span className={cn("absolute -left-[37px] top-0 inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold text-white", accent?.icon)}>{step.step}</span>
                <h3 className="font-semibold leading-7">{step.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{step.detail}</p>
                {(task || tool) && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {task && <Button asChild size="sm" variant="outline"><Link to={`/ai-cpg-tools/prompt-builder/${task.id}`}><Wand2 className="mr-1.5 h-3.5 w-3.5" />{task.title}</Link></Button>}
                    {tool && <Button asChild size="sm" variant="ghost"><a href={tool.url} target="_blank" rel="noopener noreferrer" onClick={() => eventsApi.track({ type: "open_tool", toolId: tool.id, context: `toolkit:${category.id}` })}>Open {tool.name} <ExternalLink className="ml-1.5 h-3.5 w-3.5" /></a></Button>}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </Section>

      {/* 3 · RECOMMENDED TOOLS */}
      <Section id="tools" number="03" title="Recommended tools" aside={<Button asChild variant="ghost" size="sm"><Link to="/ai-cpg-tools/tools">Full directory <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>}>
        <p className="mb-4 text-sm text-muted-foreground">For this job, use the <strong>Recommended</strong> tool first. The others are for specific situations noted on each card.</p>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {page.recommendedTools.map((rt) => {
            const tool = TOOL_BY_ID[rt.toolId];
            return tool ? <ToolCard key={rt.toolId} tool={tool} recommended={!!rt.primary} useFor={rt.useFor} whatToType={rt.whatToType} context={`toolkit:${category.id}`} /> : null;
          })}
        </div>
      </Section>

      {/* 4 · PROMPTS */}
      <Section id="prompts" number="04" title="Prompts" aside={<Badge variant="secondary">{allTasks.length} in this toolkit</Badge>}>
        <p className="mb-4 text-sm text-muted-foreground">
          <strong>Copy Prompt</strong> gives you the prompt {hasProfile ? <>pre-filled with <strong>{profile.brandName}</strong>'s brand profile</> : "with [bracketed] placeholders to fill in"}. <strong>Customize</strong> opens the guided form.
        </p>
        <div className="space-y-3">
          {featured.map((task) => (
            <PromptRow key={task.id} task={task} profile={hasProfile ? profile : null} category={category} />
          ))}
        </div>
        {more.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="w-full text-xs font-semibold uppercase tracking-wide text-muted-foreground">More {category.short} prompts</span>
            {more.map((t) => <Button key={t.id} asChild size="sm" variant="outline"><Link to={`/ai-cpg-tools/prompt-builder/${t.id}`}>{t.title}</Link></Button>)}
          </div>
        )}
      </Section>

      {/* 5 · EXAMPLES */}
      <Section id="examples" number="05" title="Examples">
        <div className="grid gap-4 md:grid-cols-2">
          {page.examples.map((ex, i) => (
            <Card key={i}>
              <CardContent className="space-y-3 p-5">
                <h3 className="font-semibold">{ex.title}</h3>
                <div className="rounded-md border border-dashed p-3 text-sm">
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Before</p>
                  <p className="text-muted-foreground">{ex.before}</p>
                </div>
                <div className={cn("rounded-md border p-3 text-sm", accent?.tile)}>
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide opacity-80">After</p>
                  <p className="text-foreground">{ex.after}</p>
                </div>
                <div className="text-sm">
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Result</p>
                  <p>{ex.result}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* Pro tips + related */}
      <div className="grid gap-4 md:grid-cols-[2fr_1fr]">
        {page.proTips?.length > 0 && (
          <Card className="bg-muted/40">
            <CardContent className="p-5">
              <div className="mb-2 flex items-center gap-2 font-semibold"><Lightbulb className="h-4 w-4 text-pink-500" /> Pro tips</div>
              <ul className="list-disc space-y-1.5 pl-5 text-sm text-muted-foreground">{page.proTips.map((t, i) => <li key={i}>{t}</li>)}</ul>
            </CardContent>
          </Card>
        )}
        {page.relatedCategories?.length > 0 && (
          <Card>
            <CardContent className="p-5">
              <div className="mb-3 font-semibold">Related toolkits</div>
              <div className="space-y-2">
                {page.relatedCategories.map((id) => {
                  const c = CATEGORY_BY_ID[id];
                  return c ? (
                    <Link key={id} to={`/ai-cpg-tools/${c.slug}`} className="flex items-center gap-2 rounded-md p-1.5 text-sm hover:bg-muted">
                      <CategoryIcon icon={c.icon} accent={c.accent} size="sm" /> {c.name}
                    </Link>
                  ) : null;
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AILabShell>
  );
}

function Section({ id, number, title, aside, children }) {
  return (
    <section id={id} className="mb-10 scroll-mt-20">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="flex items-baseline gap-3 text-xl font-bold tracking-tight"><span className="text-sm font-mono text-muted-foreground">{number}</span>{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

function PromptRow({ task, profile, category }) {
  const [open, setOpen] = React.useState(false);
  const text = renderWithPlaceholders(task, profile);
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h3 className="font-semibold">{task.title}</h3>
            <p className="text-sm text-muted-foreground">{task.description}</p>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <CopyButton text={text} size="sm" onCopied={() => eventsApi.track({ type: "copy_prompt", taskId: task.id, context: `toolkit:${category.id}` })} />
            <Button asChild size="sm" variant="outline"><Link to={`/ai-cpg-tools/prompt-builder/${task.id}`}><SlidersHorizontal className="mr-1.5 h-3.5 w-3.5" /> Customize</Link></Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)}>{open ? "Hide" : "Preview"}</Button>
          </div>
        </div>
        {open && <pre className="mt-3 max-h-80 overflow-auto whitespace-pre-wrap rounded-md bg-muted/50 p-3 font-mono text-[12px] leading-relaxed">{text}</pre>}
      </CardContent>
    </Card>
  );
}
